import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityAction, type ActivityLogToken } from '@sleed/bizcommerce-api';
import type { SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDateTime, toIsoUtc } from '~/lib/format-date';
import { activityLogFields } from '~/hooks/use-activity-log';
import { ActionBadge, ActorCell, ActorPermissionsCell, ChangedFieldsCell, EntityCell, formatChangedFields, WhenCell } from './cells';

/** Every ActivityAction, in enum order, with its locale key. Only these reach the grid today. */
export const ACTION_LABEL_KEYS: Record<number, string> = {
	[ActivityAction.Insert]: 'insert',
	[ActivityAction.Update]: 'update',
	[ActivityAction.Delete]: 'delete',
	[ActivityAction.Restore]: 'restore',
	[ActivityAction.Login]: 'login',
	[ActivityAction.Logout]: 'logout',
	[ActivityAction.LoginFailed]: 'loginFailed',
	[ActivityAction.ImpersonationStart]: 'impersonationStart'
};

/** Translated action label, falling back to the raw value for an action the UI does not know yet. */
export function useActionLabel() {
	const { t } = useTranslation();
	return useCallback(
		(value: number | undefined) => {
			const key = value === undefined ? undefined : ACTION_LABEL_KEYS[value];
			return key ? t(`system.activityLog.actions.${key}`) : String(value ?? '');
		},
		[t]
	);
}

/**
 * Column definitions of the activity log grid. Column keys are the token's json names, which means a
 * sort key doubles as the server-side selector.
 */
export function useActivityLogColumns(): SystemGridColumn<ActivityLogToken>[] {
	const { t } = useTranslation();
	const actionLabel = useActionLabel();

	return useMemo(
		() => [
			{
				key: activityLogFields.occurredOn,
				header: t('system.activityLog.columns.when'),
				width: '150px',
				sortValue: a => (a.occ ? String(a.occ) : ''),
				// Always absolute in the export: a spreadsheet outlives "2 hours ago".
				exportValue: a => formatDateTime(toIsoUtc(a.occ)),
				render: a => <WhenCell occurredOn={a.occ} />
			},
			{
				// Actor and the role held at action time in one column: a role alone identifies nobody, and a
				// name without the role it held is exactly what this log exists to avoid. Keyed on `anm` so the
				// server still sorts on the actor name; the role stays sortable via its own hidden column.
				key: activityLogFields.actorName,
				header: t('system.activityLog.columns.actorRole'),
				width: 'minmax(0,1fr)',
				sortValue: a => a.anm ?? '',
				exportValue: a => [a.anm, a.arnm].filter(Boolean).join(' / '),
				render: a => <ActorCell row={a} />
			},
			{
				// The action badge rides along in the entity cell — see EntityCell.
				key: activityLogFields.entityName,
				header: t('system.activityLog.columns.entity'),
				width: 'minmax(0,1fr)',
				sortValue: a => a.enm ?? '',
				exportValue: a => [actionLabel(a.act), a.enm, a.edn, a.ekey, a.eid ? `#${a.eid}` : null].filter(Boolean).join(' / '),
				render: a => <EntityCell row={a} actionLabel={actionLabel(a.act)} />
			},
			{
				key: activityLogFields.action,
				header: t('system.activityLog.columns.action'),
				width: '160px',
				// Folded into the entity cell as a badge; kept available in the column selector for anyone who
				// wants to sort by action.
				defaultVisible: false,
				sortValue: a => a.act ?? 0,
				exportValue: a => actionLabel(a.act),
				render: a => <ActionBadge action={a.act} label={actionLabel(a.act)} />
			},
			{
				key: activityLogFields.actorRoleName,
				header: t('system.activityLog.columns.role'),
				width: '160px',
				// Folded into the actor cell; kept as a hidden column so it stays sortable on its own.
				defaultVisible: false,
				sortValue: a => a.arnm ?? '',
				exportValue: a => a.arnm ?? '',
				render: a => <span className="truncate text-xs text-foreground-muted">{a.arnm || '—'}</span>
			},
			{
				key: 'chg',
				header: t('system.activityLog.columns.changedFields'),
				width: '220px',
				// The whole list in the export, where there is no dialog to open.
				exportValue: a => formatChangedFields(a.chg),
				render: a => <ChangedFieldsCell fields={a.chg} subject={a.edn || a.ekey || a.enm} />
			},
			{
				key: 'perms',
				header: t('system.activityLog.columns.permissions'),
				width: '240px',
				defaultVisible: false,
				exportValue: a => (a.perms ?? []).join(', '),
				render: a => <ActorPermissionsCell permissions={a.perms} />
			},
			{
				key: 'ip',
				header: t('system.activityLog.columns.ip'),
				width: '140px',
				// Visible by default since BW-4412 φάση 5: this grid replaced the UserActivities page, whose
				// four columns were email / login / logout / IP.
				exportValue: a => a.ip ?? '',
				render: a => <span className="font-mono text-xs text-foreground-muted">{a.ip || '—'}</span>
			},
			{
				key: 'ep',
				header: t('system.activityLog.columns.endpoint'),
				width: '260px',
				defaultVisible: false,
				exportValue: a => a.ep ?? '',
				render: a => <span className="truncate font-mono text-xs text-foreground-muted">{a.ep || '—'}</span>
			}
		],
		[t, actionLabel]
	);
}
